import { useState } from 'react'
import { MoreHorizontal, Plus, Pencil, Trash2, Pause, Play, CheckCircle } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { GoalTypeIcon } from './GoalTypeIcon'
import { ContributionDialog } from './ContributionDialog'
import type { Goal } from '@/types'
import { GOAL_TYPE_INFO } from '@/types/goals.types'
import { formatCurrency } from '@/lib/utils'
import { calculateGoalProgress, isGoalOnTrack } from '@/lib/achievements'
import { cn } from '@/lib/utils'

interface GoalCardProps {
  goal: Goal
  onEdit: (goal: Goal) => void
  onDelete: (goalId: string) => void
  onContribute: (goalId: string, amount: number, note: string) => void
  onStatusChange: (goalId: string, status: Goal['status']) => void
}

export function GoalCard({
  goal,
  onEdit,
  onDelete,
  onContribute,
  onStatusChange,
}: GoalCardProps) {
  const [showContribution, setShowContribution] = useState(false)
  
  const progress = calculateGoalProgress(goal)
  const onTrack = isGoalOnTrack(goal)
  const typeInfo = GOAL_TYPE_INFO[goal.type]
  
  const formatDeadline = (deadline: string | null) => {
    if (!deadline) return 'No deadline'
    const date = new Date(deadline)
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }

  const getStatusBadge = () => {
    switch (goal.status) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>
      case 'paused':
        return <Badge variant="secondary">Paused</Badge>
      case 'abandoned':
        return <Badge variant="destructive">Abandoned</Badge>
      default:
        if (progress >= 100) {
          return <Badge variant="success">Ready!</Badge>
        }
        if (onTrack === false) {
          return <Badge variant="warning">Behind</Badge>
        }
        if (onTrack === true) {
          return <Badge variant="default">On Track</Badge>
        }
        return null
    }
  }

  const isActive = goal.status === 'active'

  return (
    <>
      <Card className={cn(
        'relative overflow-hidden transition-all hover:shadow-md',
        !isActive && 'opacity-75'
      )}>
        {/* Color accent bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-1"
          style={{ backgroundColor: goal.color }}
        />
        
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <GoalTypeIcon type={goal.type} size="lg" showBackground />
              <div>
                <h3 className="font-semibold leading-tight">{goal.name}</h3>
                <p className="text-sm text-muted-foreground">{typeInfo.label}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {getStatusBadge()}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(goal)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit Goal
                  </DropdownMenuItem>
                  {isActive && (
                    <DropdownMenuItem onClick={() => onStatusChange(goal.id, 'paused')}>
                      <Pause className="mr-2 h-4 w-4" />
                      Pause Goal
                    </DropdownMenuItem>
                  )}
                  {goal.status === 'paused' && (
                    <DropdownMenuItem onClick={() => onStatusChange(goal.id, 'active')}>
                      <Play className="mr-2 h-4 w-4" />
                      Resume Goal
                    </DropdownMenuItem>
                  )}
                  {isActive && progress >= 100 && (
                    <DropdownMenuItem onClick={() => onStatusChange(goal.id, 'completed')}>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Mark Complete
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => onDelete(goal.id)}
                    className="text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Goal
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Progress Section */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{progress.toFixed(0)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
            <div className="flex justify-between text-sm">
              <span className="font-semibold">{formatCurrency(goal.currentAmount)}</span>
              <span className="text-muted-foreground">of {formatCurrency(goal.targetAmount)}</span>
            </div>
          </div>
          
          {/* Deadline */}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Deadline</span>
            <span>{formatDeadline(goal.deadline)}</span>
          </div>
          
          {/* Linked Account Indicator */}
          {goal.linkedAccountId && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-2 w-2 rounded-full bg-green-500" />
              Linked to {goal.linkedAccountType === 'investment' ? 'investment' : 'debt'}
            </div>
          )}
          
          {/* Actions */}
          {isActive && !goal.linkedAccountId && (
            <Button 
              onClick={() => setShowContribution(true)} 
              className="w-full"
              size="sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Contribution
            </Button>
          )}
          
          {goal.status === 'completed' && (
            <div className="flex items-center justify-center gap-2 text-success text-sm font-medium">
              <CheckCircle className="h-4 w-4" />
              Goal Achieved!
            </div>
          )}
        </CardContent>
      </Card>
      
      <ContributionDialog
        open={showContribution}
        onOpenChange={setShowContribution}
        goalName={goal.name}
        remaining={goal.targetAmount - goal.currentAmount}
        onSubmit={(amount, note) => {
          onContribute(goal.id, amount, note)
          setShowContribution(false)
        }}
      />
    </>
  )
}
